import { Injectable } from '@angular/core';
import { BcRestApi } from 'common/api/rest-api';
import { WorkItemTypes, WorkItemTypeValue } from 'common/workitems/constants/workitem-types';
import { BcAuth } from 'common/auth/auth.service';
import { AssigneeTypes } from 'common/common/constants/boldchat-enums';
import { PCacheable } from 'ngx-cacheable';
import { NotificationBarService } from 'shared/services/notification/notification-bar.service';
import { StatusCodes } from 'http-status-codes';

@Injectable()
export class QueueViewTabService {
  constructor(private _bcRestApi: BcRestApi,
              private _bcAuth: BcAuth,
              private _notificationService: NotificationBarService) {
  }

  @PCacheable()
  public async getFolders(workItemType: string) {
    return this._bcRestApi.getFolders(this._getWorkItemTypeValue(workItemType));
  }

  public async assignItems(workItemType: string, itemIds: string[], escalateChat?: boolean): Promise<void> {
    const assignee = this._bcAuth.getSessionUserId();
    if (escalateChat) {
      await this._escalateBotChat(itemIds, assignee);
    } else {
      switch (workItemType) {
        case WorkItemTypes.CHAT:
          await this._bcRestApi.assignChats(itemIds, assignee, AssigneeTypes.OPERATOR);
          break;
        case WorkItemTypes.EMAIL:
          await this._bcRestApi.assignEmails(itemIds, assignee, AssigneeTypes.OPERATOR);
          break;
        case WorkItemTypes.SMS:
          await this._bcRestApi.assignTextChats(itemIds, assignee, AssigneeTypes.OPERATOR);
          break;
        case WorkItemTypes.FACEBOOK:
        case WorkItemTypes.ASYNC_MESSAGE:
        default:
          throw new Error(`Unhandled type (${workItemType})`);
      }
    }
  }

  public async endItems(workItemType: string, itemIds: string[]): Promise<void> {
    switch (workItemType) {
      case WorkItemTypes.CHAT:
        await this._bcRestApi.endChats(itemIds);
        break;
      case WorkItemTypes.EMAIL:
        await this._bcRestApi.closeEmailThreads(itemIds);
        break;
      case WorkItemTypes.SMS:
        await this._bcRestApi.endTextChats(itemIds);
        break;
      case WorkItemTypes.FACEBOOK:
      case WorkItemTypes.ASYNC_MESSAGE:
      default:
        throw new Error(`Unhandled type (${workItemType})`);
    }
  }

  public async reopenEmailThreads(emailThreadIds: string[]): Promise<void> {
    return this._bcRestApi.reopenEmailThreads(emailThreadIds);
  }

  private _getWorkItemTypeValue(workItemType: string): WorkItemTypeValue {
    switch (workItemType) {
      case WorkItemTypes.CHAT:
        return WorkItemTypeValue.CHAT;
      case WorkItemTypes.EMAIL:
        return WorkItemTypeValue.EMAIL;
      case WorkItemTypes.SMS:
        return WorkItemTypeValue.SMS;
      case WorkItemTypes.FACEBOOK:
        return WorkItemTypeValue.FACEBOOK;
      case WorkItemTypes.ASYNC_MESSAGE:
        return WorkItemTypeValue.ASYNC_MESSAGE;
      default:
        throw new Error(`Unhandled type (${workItemType})`);
    }
  }


  private async _escalateBotChat(itemIds: string[], assignee: string) {
    try {
      await this._bcRestApi.escalateChats(itemIds, assignee, AssigneeTypes.OPERATOR);
    } catch (error) {
      if (error.status === StatusCodes.NOT_FOUND) {
        this._notificationService.addNotification({ id: 'escalation-failed', content: 'queueViewTab.missingVisitor' });
      } else {
        throw error;
      }
    }
  }
}
